import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Loader, Modal } from '@ur-solutions/react-components'
import { X } from 'lucide-react'
import { useMemo } from 'react'
import { isMobile } from 'react-device-detect'

import { GET_SPECIFIC_CONDITIONS } from '@/modules/activities'
import { InfringementDetailV1 } from '@/modules/infringements/components'
import {
  INFRINGEMENT_ACTIVITIES_EXTEND_AFTER_MINUTES,
  INFRINGEMENT_ACTIVITIES_EXTEND_BEFORE_MINUTES,
} from '@/modules/infringements/consts'
import {
  type FerrySpecificConditionType,
  getFerryConditionsWindow,
} from '@/modules/infringements/ferry-conditions'
import { INFRINGEMENT_QUERY } from '@/modules/infringements/queries'
import { getNodes } from '@/utils/graphql'

const FERRY_SPECIFIC_CONDITION_TYPES = new Set<FerrySpecificConditionType>([
  'FERRY',
  'FERRY_START',
  'FERRY_END',
])

function isFerrySpecificConditionType(
  value: string
): value is FerrySpecificConditionType {
  return FERRY_SPECIFIC_CONDITION_TYPES.has(value as FerrySpecificConditionType)
}

// `cache-and-network` returns the cached result immediately then
// refetches in the background. Combined with the modal's open/close
// lifecycle this means re-opening the same infringement paints
// instantly with whatever Apollo has cached.
const FETCH_POLICY = 'cache-and-network'

export interface InfringementDetailModalProps {
  infringementId: string | null
  open: boolean
  onClose: () => void
}

/**
 * Modal wrapper around `InfringementDetailV1`. Fetches the full
 * infringement (with surrounding activity context) plus the
 * ferry-specific conditions across the gantt's span so the
 * detail view can render the ferry overlay.
 *
 * Both queries are skipped while the modal is closed so opening the
 * page doesn't fire requests for an unselected detail.
 */
export const InfringementDetailModal: React.FC<
  InfringementDetailModalProps
> = ({ infringementId, open, onClose }) => {
  const translations = useTranslate({
    close: 'common.close',
    infringement: 'common.infringement',
    loading: 'common.loading-ellipsis',
  })

  const { data, loading } = useQuery(INFRINGEMENT_QUERY, {
    skip: !open || !infringementId,
    fetchPolicy: FETCH_POLICY,
    nextFetchPolicy: 'cache-first',
    variables: {
      id: infringementId!,
      activitiesExtendBeforeMinutes:
        INFRINGEMENT_ACTIVITIES_EXTEND_BEFORE_MINUTES,
      activitiesExtendAfterMinutes:
        INFRINGEMENT_ACTIVITIES_EXTEND_AFTER_MINUTES,
    },
  })

  const infringement = data?.infringement ?? null
  const userId = infringement?.user.id ?? null
  const conditionsWindow = useMemo(
    () => (infringement ? getFerryConditionsWindow(infringement) : null),
    [infringement]
  )

  const { data: specificConditionsData, loading: conditionsLoading } = useQuery(
    GET_SPECIFIC_CONDITIONS,
    {
      skip: !open || !userId || !conditionsWindow,
      fetchPolicy: FETCH_POLICY,
      nextFetchPolicy: 'cache-first',
      variables: {
        userId,
        dateStart: conditionsWindow?.dateStart,
        dateEnd: conditionsWindow?.dateEnd,
      },
    }
  )

  const ferrySpecificConditions = useMemo(
    () =>
      getNodes(specificConditionsData?.allSpecificConditions)
        .filter(
          (node): node is typeof node & { type: FerrySpecificConditionType } =>
            isFerrySpecificConditionType(node.type)
        )
        .map(node => ({
          id: node.id,
          timestamp: node.timestamp,
          type: node.type,
        })),
    [specificConditionsData?.allSpecificConditions]
  )

  return (
    <Modal
      open={open}
      offset={isMobile ? 8 : 36}
      placement="top-center"
      direction="from-top"
      onClose={onClose}
    >
      <div className="flex max-h-[calc(100vh-72px)] w-[min(1200px,calc(100vw-16px))] flex-col overflow-hidden rounded-xl bg-white shadow-2xl md:w-[min(1200px,calc(100vw-72px))]">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 md:px-6">
          <h2 className="text-base font-semibold text-gray-900">
            {translations.infringement}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
            aria-label={translations.close}
          >
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto px-4 py-4 md:px-6 md:py-6">
          {loading || (infringement && conditionsLoading) ? (
            <div className="flex min-h-48 items-center justify-center gap-3 text-sm text-gray-500">
              <Loader.Spinner />
              <span>{translations.loading}</span>
            </div>
          ) : infringement ? (
            <InfringementDetailV1
              infringement={infringement}
              ferrySpecificConditions={ferrySpecificConditions}
              wrapperProps={{ style: { background: 'transparent' } }}
            />
          ) : null}
        </div>
      </div>
    </Modal>
  )
}
